import { useRef, useState } from 'react';
import { Box, Flex, IconButton, Textarea, useToast } from '@chakra-ui/react';
import { FiPaperclip, FiSend } from 'react-icons/fi';
import { authAPI } from '../../services/api';

const MAX_TEXTAREA_H = 96;

// 訊息輸入列：文字 + 圖片附加。
// 圖片流程：選檔 → 立即上傳（復用既有 /communication-books/upload，教職員權限已涵蓋帶班老師/行政/管理員）
// → 逐張各自送出一則 content_type='image' 訊息（後端一則訊息僅一張圖，故多選時依序送出）。
// 無「送出前預覽/取消」中間態，選了就直接送，符合手機「少打字、少步驟」原則。
//
// [B3 誠實記錄] allowImage=false 時完全不渲染附加圖片按鈕/檔案選取器。家長對話室會傳 false：
// /communication-books/upload 的權限依賴項是 get_current_active_teacher（app/security.py），
// 明確排除 parent 角色，家長呼叫會 403。這是後端既有範圍外的權限落差，不在本工作包修——
// 用這個 prop 讓家長端先不顯示會失敗的按鈕，避免家長點了卻收到莫名其妙的錯誤。
// [Stage3 整合] uploadFn：可選的自訂上傳函式（FormData → Promise<res>）。家長對話室傳入
// chat 專用上傳端點（E1 新增，旗標＋對話參與者雙門）；未傳入時沿用既有聯絡簿上傳
// （老師端行為逐字不變）。
// [2026-08-07] safeAreaBottom：這一列下方是否還有別的東西擋著。
// 家長對話室的底部導航會隱藏（見 ParentLayout），輸入列就是畫面最底部，必須自己留 iOS 安全區；
// 老師 PWA 的對話室下方還有常駐的底部導航（它自己已經 pb="env(safe-area-inset-bottom)"），
// 若這裡再留一次，安全區等於被算兩次 → 輸入列與導航列之間出現一大塊空白（user 實機回報）。
export default function Composer({ onSend, disabled, allowImage = true, uploadFn = null, safeAreaBottom = true }) {
    const [text, setText] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const taRef = useRef(null);
    const toast = useToast();

    // 送文字已改成樂觀更新、不再等待網路，故不需要「送出中」狀態；
    // 圖片仍需 uploading（上傳是真的要等，且期間不該重複選檔）。
    const busy = uploading || disabled;

    // 輸入框只有在內容真的超過上限時才可捲動。
    // 【為什麼要動態切 —— 2026-08-13 user 實機回報】
    // 原本固定 overflowY:auto。在 iOS 上，手指按著一個「有 auto 但其實沒東西可捲」的元素
    // 上下拖，手勢會沿著捲動鏈往上冒泡，Safari 就改去平移 visual viewport；
    // 外殼又綁著 vv.offsetTop，於是整個畫面跟著手指上下跑 —— user 描述的
    // 「點著輸入框上下滑動畫面也會跟著抖動」「感覺還是重新渲染」。
    // user 的判斷是對的：正常輸入框不該觸發滑動。
    const syncTextareaScroll = (el) => {
        el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_H ? 'auto' : 'hidden';
    };

    const resetTextareaHeight = () => {
        if (!taRef.current) return;
        taRef.current.style.height = 'auto';
        syncTextareaScroll(taRef.current);
    };

    const handleInput = (e) => {
        const el = e.target;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, MAX_TEXTAREA_H) + 'px';
        syncTextareaScroll(el);
    };

    // 樂觀更新（比照 LINE）：不等網路回來，按下送出就立刻清空輸入框，
    // 訊息由上層 useOptimisticSend 立即插進畫面（半透明＝傳送中）。
    // 失敗不再跳 toast，改成氣泡旁的紅色驚嘆號（點一下重送）——toast 會一閃即逝、
    // 且無法對應是哪一則，訊息本身帶狀態才是正確的呈現方式。
    const handleSendText = () => {
        const trimmed = text.trim();
        if (!trimmed || busy) return;
        setText('');
        resetTextareaHeight();
        // 送出後把焦點留在輸入框，鍵盤才不會收起來（user 2026-08-18 要求：
        // 連續回訊息時每送一則就要重新點一次輸入框，很干擾）。
        // 這是保險：真正防止失焦的是送出鍵的 onMouseDown preventDefault（見下方），
        // 但某些 WebView 仍會移走焦點，所以再明確要回來一次。
        taRef.current?.focus();
        // 上層的 send() 契約是「永不 reject」，這裡仍防禦性接住，避免任何情況下的 unhandled rejection
        Promise.resolve(onSend({ content_type: 'text', text: trimmed })).catch(() => {});
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendText();
        }
    };

    const handlePickImage = () => {
        if (busy) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // 允許連續選同一張
        if (!files.length) return;

        setUploading(true);
        try {
            const formData = new FormData();
            files.forEach((f) => formData.append('files', f));
            const res = uploadFn ? await uploadFn(formData) : await authAPI.communicationBook.uploadPhoto(formData);
            const urls = res.data?.urls || [];
            for (const url of urls) {
                // 依序送出，避免同時多個 POST 造成後端 last_message_* 互相覆蓋的順序問題
                // eslint-disable-next-line no-await-in-loop
                await onSend({ content_type: 'image', image_url: url });
            }
            if (!urls.length) {
                toast({ title: '圖片上傳失敗', status: 'error', duration: 2000 });
            }
        } catch (err) {
            toast({ title: '圖片上傳失敗', status: 'error', duration: 2000 });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box flexShrink={0} bg="white" borderTop="1px solid" borderColor="neutral.200"
            px={2} py={2}
            sx={{ paddingBottom: safeAreaBottom ? 'calc(env(safe-area-inset-bottom) + 8px)' : '8px' }}>
            <Flex align="flex-end" gap={2}>
                {allowImage && (
                    <>
                        <IconButton
                            aria-label="附加圖片"
                            icon={<FiPaperclip />}
                            variant="ghost"
                            color="neutral.500"
                            isLoading={uploading}
                            isDisabled={busy && !uploading}
                            onClick={handlePickImage}
                            flexShrink={0}
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            onChange={handleFileChange}
                        />
                    </>
                )}
                <Textarea
                    ref={taRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    placeholder="輸入訊息…"
                    rows={1}
                    resize="none"
                    maxH={`${MAX_TEXTAREA_H}px`}
                    // 預設不可捲（空的輸入框沒東西可捲）；超過上限時由 syncTextareaScroll 改成 auto。
                    // overscrollBehavior:contain＝就算真的在捲，捲到頭也不把手勢傳給外層。
                    overflowY="hidden"
                    sx={{ overscrollBehavior: 'contain' }}
                    bg="neutral.50"
                    borderRadius="xl"
                    fontSize="sm"
                    isDisabled={disabled}
                    py={2}
                />
                <IconButton
                    aria-label="送出"
                    icon={<FiSend />}
                    colorScheme="brand"
                    borderRadius="full"
                    isDisabled={busy || !text.trim()}
                    onClick={handleSendText}
                    // 【鍵盤要留著】按鈕預設會在 mousedown 時取得焦點，焦點一離開輸入框，
                    // 系統就把鍵盤收起來。preventDefault 只擋「轉移焦點」這件事，
                    // 不影響隨後的 click，所以送出照常運作。
                    // 使用者要收鍵盤時，點輸入框以外的地方或按系統返回鍵即可——
                    // 那兩條路徑本來就會讓輸入框失焦，我們沒有去攔。
                    onMouseDown={(e) => e.preventDefault()}
                    flexShrink={0}
                />
            </Flex>
        </Box>
    );
}
